import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  businessTypes,
  countries,
  digitalAssetDemandOptions,
  individualProfiles,
  individualVolumeOptions,
  merchantInterests,
  merchantVolumeOptions,
  pilotReadinessOptions,
  pilotUseCaseOptions,
  primaryPaymentMethods,
  settlementMethodOptions,
} from "@/data/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildInitialState = (role) => ({
  role,
  full_name: "",
  email: "",
  country: "Nigeria",
  usage_plan: "",
  individual_roles: [],
  average_digital_asset_volume: "",
  business_name: "",
  business_type: "",
  website_or_social: "",
  estimated_monthly_transaction_volume: "",
  interested_in: [],
  current_primary_payment_methods: [],
  digital_asset_payment_demand: "",
  preferred_settlement_method: "",
  expected_pilot_use_case: "",
  pilot_readiness: "",
});

const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const fieldClassName =
  "h-12 rounded-2xl border-[#303B67] bg-[#08133E] px-4 text-white shadow-sm transition focus-visible:border-[#6AE32F] focus-visible:ring-2 focus-visible:ring-[#6AE32F]/30";

export default function WaitlistForm({ role, onRoleChange, trackEvent }) {
  const [formData, setFormData] = useState(buildInitialState(role));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [verificationNotice, setVerificationNotice] = useState("");
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  useEffect(() => {
    setFormData((current) => ({ ...current, role }));
  }, [role]);

  const roleSummary = useMemo(
    () =>
      role === "merchant"
        ? "Tell us about your business, current payment setup, and readiness to test Koineckta."
        : "Tell us how you want to use digital value for everyday payments.",
    [role],
  );

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const toggleListValue = (field, value) => {
    setFormData((current) => {
      const nextValues = current[field].includes(value)
        ? current[field].filter((item) => item !== value)
        : [...current[field], value];
      return { ...current, [field]: nextValues };
    });
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const switchRole = (nextRole) => {
    const preservedFields = {
      full_name: formData.full_name,
      email: formData.email,
      country: formData.country,
      usage_plan: formData.usage_plan,
    };
    onRoleChange(nextRole);
    setFormData({ ...buildInitialState(nextRole), ...preservedFields, role: nextRole });
    setErrors({});
    setIsSuccess(false);
    setPendingChallenge(null);
    setVerificationCode("");
    setVerificationError("");
    setVerificationNotice("");
    trackEvent?.("waitlist_role_selected", { role: nextRole, location: "waitlist-form" });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.full_name.trim()) nextErrors.full_name = "Full name is required.";
    if (!formData.email.trim()) nextErrors.email = "Email address is required.";
    else if (!emailPattern.test(formData.email.trim())) nextErrors.email = "Please enter a valid email address.";
    if (!formData.country) nextErrors.country = "Please choose a country.";
    if (role === "merchant") {
      if (!formData.business_name.trim()) nextErrors.business_name = "Business name is required.";
      if (formData.current_primary_payment_methods.length === 0) nextErrors.current_primary_payment_methods = "Choose at least one current payment method.";
      if (!formData.digital_asset_payment_demand) nextErrors.digital_asset_payment_demand = "Choose the closest answer.";
      if (!formData.preferred_settlement_method) nextErrors.preferred_settlement_method = "Choose a preferred settlement method.";
      if (!formData.expected_pilot_use_case) nextErrors.expected_pilot_use_case = "Choose an expected pilot use case.";
      if (!formData.pilot_readiness) nextErrors.pilot_readiness = "Choose your pilot readiness.";
    }
    if (role === "individual" && formData.individual_roles.length === 0) {
      nextErrors.individual_roles = "Choose at least one option that describes you.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    setErrors(nextErrors);
    setIsSuccess(false);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/waitlist/request-code`, {
        ...formData,
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        usage_plan: formData.usage_plan.trim(),
        business_name: formData.business_name.trim(),
        business_type: formData.business_type,
        website_or_social: formData.website_or_social.trim(),
        estimated_monthly_transaction_volume: formData.estimated_monthly_transaction_volume,
        average_digital_asset_volume: formData.average_digital_asset_volume,
      });
      setPendingChallenge(response.data);
      setVerificationCode("");
      setVerificationError("");
      setVerificationNotice(response.data.message);
      trackEvent?.("waitlist_code_requested", { role });
    } catch (error) {
      setErrors({
        submit:
          error?.response?.data?.detail ||
          "Something went wrong while saving your details. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!pendingChallenge) return;
    if (!/^\d{6}$/.test(verificationCode.trim())) {
      setVerificationError("Please enter the 6-digit verification code.");
      return;
    }
    setIsVerifyingCode(true);
    setVerificationError("");
    try {
      const response = await axios.post(`${API}/waitlist/verify-code`, {
        signup_id: pendingChallenge.signup_id,
        code: verificationCode.trim(),
      });
      setPendingChallenge(null);
      setVerificationCode("");
      setVerificationNotice("");
      setIsSuccess(true);
      setFormData(buildInitialState(role));
      trackEvent?.("waitlist_submitted", { role, verified: true });
      setErrors({});
      setVerificationError(response.data.message);
      setTimeout(() => setVerificationError(""), 1200);
    } catch (error) {
      setVerificationError(error?.response?.data?.detail || "Could not verify the code. Please try again.");
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingChallenge) return;
    setIsResendingCode(true);
    setVerificationError("");
    try {
      const response = await axios.post(`${API}/waitlist/resend-code`, {
        signup_id: pendingChallenge.signup_id,
      });
      setPendingChallenge(response.data);
      setVerificationNotice(response.data.message);
      trackEvent?.("waitlist_code_resent", { role });
    } catch (error) {
      setVerificationError(error?.response?.data?.detail || "Could not resend the code. Please try again.");
    } finally {
      setIsResendingCode(false);
    }
  };

  const closeVerificationModal = () => {
    setPendingChallenge(null);
    setVerificationCode("");
    setVerificationError("");
    setVerificationNotice("");
  };

  return (
    <>
      <Card className="overflow-hidden rounded-[30px] border border-[#3ABDBC]/22 bg-[#08133E]/90 shadow-[0_40px_90px_-40px_rgba(1,11,48,0.8)] backdrop-blur-xl" data-testid="waitlist-form-card">
        <CardHeader className="space-y-4 border-b border-[#26345F] pb-6">
          <div className="inline-flex w-fit rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700" data-testid="waitlist-form-label">
            {role === "merchant" ? "Merchant pilot application" : "Individual early access"}
          </div>
          <div className="space-y-2">
            <CardTitle className="font-display text-3xl text-slate-950" data-testid="waitlist-form-title">
              {role === "merchant" ? "Join the Koineckta Merchant Pilot" : "Join Koineckta Early Access"}
            </CardTitle>
            <p className="text-sm leading-6 text-slate-600" data-testid="waitlist-form-summary">
              {roleSummary}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2" data-testid="waitlist-role-toggle">
            {["individual", "merchant"].map((option) => {
              const active = role === option;
              const label = option === "individual" ? "I’m an Individual" : "I’m a Merchant / Business";
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => switchRole(option)}
                  className={`rounded-2xl border px-4 py-4 text-left text-sm font-semibold transition ${
                    active
                      ? "border-[#6AE32F] bg-[#6AE32F] text-[#010B30] shadow-[0_0_22px_rgba(106,227,47,0.18)]"
                      : "border-[#303B67] bg-[#121E4E] text-[#DCE4F4] hover:border-[#3ABDBC] hover:bg-[#142355]"
                  }`}
                  data-testid={`waitlist-role-${option}-button`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </CardHeader>

        <CardContent className="p-6 sm:p-8">
          <form className="space-y-6" onSubmit={handleSubmit} noValidate data-testid="waitlist-form">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="full_name" data-testid="full-name-label">
                Full Name
              </label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(event) => updateField("full_name", event.target.value)}
                placeholder="Enter your full name"
                className={fieldClassName}
                aria-invalid={Boolean(errors.full_name)}
                aria-describedby={errors.full_name ? "full-name-error" : undefined}
                data-testid="full-name-input"
              />
              {errors.full_name && <p id="full-name-error" className="text-sm text-red-300" role="alert" data-testid="full-name-error">{errors.full_name}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="email" data-testid="email-label">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(event) => updateField("email", event.target.value)}
                placeholder="you@example.com"
                className={fieldClassName}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
                data-testid="email-input"
              />
              {errors.email && <p id="email-error" className="text-sm text-red-300" role="alert" data-testid="email-error">{errors.email}</p>}
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="country" data-testid="country-label">
                Country
              </label>
              <select
                id="country"
                value={formData.country}
                onChange={(event) => updateField("country", event.target.value)}
                className={`${fieldClassName} w-full`}
                data-testid="country-select"
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {errors.country && <p className="text-sm text-red-600" data-testid="country-error">{errors.country}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="usage_plan" data-testid="usage-plan-label">
                How do you plan to use Koineckta?
              </label>
              <Textarea
                id="usage_plan"
                value={formData.usage_plan}
                onChange={(event) => updateField("usage_plan", event.target.value)}
                placeholder="Tell us about your use case, payment flow, or market interest"
                className="min-h-[120px] rounded-2xl border-[#303B67] bg-[#08133E] px-4 py-3 text-white shadow-sm focus-visible:border-[#6AE32F] focus-visible:ring-2 focus-visible:ring-[#6AE32F]/30"
                data-testid="usage-plan-textarea"
              />
            </div>
          </div>

          {role === "individual" ? (
            <div className="grid gap-5 md:grid-cols-2" data-testid="individual-form-fields">
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700" data-testid="individual-role-group-label">I am a</p>
                <div className="grid gap-3 rounded-3xl border border-slate-200 bg-slate-50/70 p-4">
                  {individualProfiles.map((option) => {
                    const slug = option.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    return (
                      <label key={option} htmlFor={`individual-role-${slug}`} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-testid={`individual-role-${slug}-label`}>
                        <Checkbox
                          id={`individual-role-${slug}`}
                          checked={formData.individual_roles.includes(option)}
                          onCheckedChange={() => toggleListValue("individual_roles", option)}
                          data-testid={`individual-role-${slug}-checkbox`}
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.individual_roles && (
                  <p className="text-sm text-red-300" role="alert" data-testid="individual-role-error">{errors.individual_roles}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700" htmlFor="individual_volume" data-testid="individual-volume-label">
                  Average monthly digital asset volume
                </label>
                <select
                  id="individual_volume"
                  value={formData.average_digital_asset_volume}
                  onChange={(event) => updateField("average_digital_asset_volume", event.target.value)}
                  className={`${fieldClassName} w-full`}
                  data-testid="individual-volume-select"
                >
                  <option value="">Select a range</option>
                  {individualVolumeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2" data-testid="merchant-form-fields">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700" htmlFor="business_name" data-testid="business-name-label">
                  Business Name
                </label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(event) => updateField("business_name", event.target.value)}
                  placeholder="Enter your business name"
                  className={fieldClassName}
                  data-testid="business-name-input"
                />
                {errors.business_name && (
                  <p className="text-sm text-red-600" data-testid="business-name-error">{errors.business_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700" htmlFor="business_type" data-testid="business-type-label">
                  Business Type
                </label>
                <select
                  id="business_type"
                  value={formData.business_type}
                  onChange={(event) => updateField("business_type", event.target.value)}
                  className={`${fieldClassName} w-full`}
                  data-testid="business-type-select"
                >
                  <option value="">Select a business type</option>
                  {businessTypes.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700" htmlFor="website_or_social" data-testid="website-social-label">
                  Website or Social Handle
                </label>
                <Input
                  id="website_or_social"
                  value={formData.website_or_social}
                  onChange={(event) => updateField("website_or_social", event.target.value)}
                  placeholder="yourstore.com or @yourbrand"
                  className={fieldClassName}
                  data-testid="website-social-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700" htmlFor="merchant_volume" data-testid="merchant-volume-label">
                  Estimated Monthly Transaction Volume
                </label>
                <select
                  id="merchant_volume"
                  value={formData.estimated_monthly_transaction_volume}
                  onChange={(event) => updateField("estimated_monthly_transaction_volume", event.target.value)}
                  className={`${fieldClassName} w-full`}
                  data-testid="merchant-volume-select"
                >
                  <option value="">Select a range</option>
                  {merchantVolumeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3 md:col-span-2">
                <p className="text-sm font-semibold text-slate-700" data-testid="merchant-interest-group-label">Interested in</p>
                <div className="grid gap-3 rounded-3xl border border-slate-200 bg-slate-50/70 p-4 md:grid-cols-3">
                  {merchantInterests.map((option) => {
                    const slug = option.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    return (
                      <label key={option} htmlFor={`merchant-interest-${slug}`} className="flex items-center gap-3 text-sm font-medium text-slate-700" data-testid={`merchant-interest-${slug}-label`}>
                        <Checkbox
                          id={`merchant-interest-${slug}`}
                          checked={formData.interested_in.includes(option)}
                          onCheckedChange={() => toggleListValue("interested_in", option)}
                          data-testid={`merchant-interest-${slug}-checkbox`}
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-5 rounded-3xl border border-[#3ABDBC]/22 bg-[#0A1948]/75 p-5 md:col-span-2" data-testid="merchant-qualification-fields">
                <div>
                  <p className="text-sm font-semibold text-slate-700" data-testid="primary-payment-methods-label">Current primary payment methods</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {primaryPaymentMethods.map((option) => {
                      const slug = option.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                      return (
                        <label key={option} htmlFor={`payment-method-${slug}`} className="flex items-center gap-3 rounded-2xl border border-[#303B67] bg-[#08133E] p-3 text-sm font-medium text-slate-700" data-testid={`payment-method-${slug}-label`}>
                          <Checkbox id={`payment-method-${slug}`} checked={formData.current_primary_payment_methods.includes(option)} onCheckedChange={() => toggleListValue("current_primary_payment_methods", option)} data-testid={`payment-method-${slug}-checkbox`} />
                          <span>{option}</span>
                        </label>
                      );
                    })}
                  </div>
                  {errors.current_primary_payment_methods && <p className="mt-2 text-sm text-red-300" role="alert" data-testid="primary-payment-methods-error">{errors.current_primary_payment_methods}</p>}
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="digital_asset_payment_demand" data-testid="digital-asset-demand-label">Do customers ask to pay in digital assets?</label>
                    <select id="digital_asset_payment_demand" value={formData.digital_asset_payment_demand} onChange={(event) => updateField("digital_asset_payment_demand", event.target.value)} className={`${fieldClassName} w-full`} data-testid="digital-asset-demand-select">
                      <option value="">Select an answer</option>
                      {digitalAssetDemandOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    {errors.digital_asset_payment_demand && <p className="text-sm text-red-300" role="alert" data-testid="digital-asset-demand-error">{errors.digital_asset_payment_demand}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="preferred_settlement_method" data-testid="settlement-method-label">Preferred settlement method</label>
                    <select id="preferred_settlement_method" value={formData.preferred_settlement_method} onChange={(event) => updateField("preferred_settlement_method", event.target.value)} className={`${fieldClassName} w-full`} data-testid="settlement-method-select">
                      <option value="">Select a method</option>
                      {settlementMethodOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    {errors.preferred_settlement_method && <p className="text-sm text-red-300" role="alert" data-testid="settlement-method-error">{errors.preferred_settlement_method}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="expected_pilot_use_case" data-testid="pilot-use-case-label">Expected pilot use case</label>
                    <select id="expected_pilot_use_case" value={formData.expected_pilot_use_case} onChange={(event) => updateField("expected_pilot_use_case", event.target.value)} className={`${fieldClassName} w-full`} data-testid="pilot-use-case-select">
                      <option value="">Select a use case</option>
                      {pilotUseCaseOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    {errors.expected_pilot_use_case && <p className="text-sm text-red-300" role="alert" data-testid="pilot-use-case-error">{errors.expected_pilot_use_case}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor="pilot_readiness" data-testid="pilot-readiness-label">Pilot readiness</label>
                    <select id="pilot_readiness" value={formData.pilot_readiness} onChange={(event) => updateField("pilot_readiness", event.target.value)} className={`${fieldClassName} w-full`} data-testid="pilot-readiness-select">
                      <option value="">Select readiness</option>
                      {pilotReadinessOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    {errors.pilot_readiness && <p className="text-sm text-red-300" role="alert" data-testid="pilot-readiness-error">{errors.pilot_readiness}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

            <div className="space-y-4 rounded-3xl border border-[#3ABDBC]/22 bg-[#0A1948]/80 p-5">
              <p className="text-sm leading-6 text-slate-700" data-testid="waitlist-consent-line">
                We’ll only use your information to share Koineckta updates and early access information.
              </p>

              {errors.submit && <p className="text-sm text-red-600" data-testid="waitlist-submit-error">{errors.submit}</p>}
              {isSuccess && (
                <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="waitlist-success-message">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  <p>Thanks for joining the Koineckta early access list. We’ll contact you with updates and launch details.</p>
                </div>
              )}

              <Button
                type="submit"
                className="h-12 w-full rounded-full bg-[#6AE32F] text-[#010B30] shadow-[0_0_24px_rgba(106,227,47,0.2)] transition hover:bg-[#82EF4D]"
                disabled={isSubmitting}
                data-testid="waitlist-submit-button"
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Sending verification code...
                  </span>
                ) : (
                  role === "merchant" ? "Join the Merchant Pilot" : "Join Early Access"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {pendingChallenge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#010B30]/82 px-4 py-6 backdrop-blur-xl" data-testid="verification-modal-overlay">
          <div className="w-full max-w-lg rounded-[30px] border border-[#3ABDBC]/28 bg-[#08133E]/96 p-6 shadow-[0_30px_90px_-35px_rgba(0,0,0,0.72)] sm:p-8" data-testid="verification-modal-card">
            <div className="space-y-3">
              <div className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-4 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700" data-testid="verification-modal-label">
                Email verification required
              </div>
              <h3 className="font-display text-3xl font-semibold text-slate-950" data-testid="verification-modal-title">
                Enter the 6-digit code sent to your email
              </h3>
              <p className="text-sm leading-7 text-slate-600" data-testid="verification-modal-description">
                We sent a unique validation code to <span className="font-semibold text-slate-950">{pendingChallenge.email}</span>. Enter it below to finish your Koineckta signup.
              </p>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-emerald-700" data-testid="verification-modal-expiry">
                Code expires at {new Date(pendingChallenge.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <Input
                inputMode="numeric"
                maxLength={6}
                value={verificationCode}
                onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="h-14 rounded-2xl border-[#303B67] bg-[#010B30] text-center text-2xl font-semibold tracking-[0.35em] text-white focus-visible:border-[#6AE32F] focus-visible:ring-[#6AE32F]/30"
                data-testid="verification-code-input"
              />
              {verificationNotice && <p className="text-sm text-sky-700" data-testid="verification-notice-message">{verificationNotice}</p>}
              {verificationError && <p className="text-sm text-red-600" data-testid="verification-error-message">{verificationError}</p>}

              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  type="button"
                  onClick={handleVerifyCode}
                  className="h-12 rounded-full bg-[#6AE32F] text-[#010B30] hover:bg-[#82EF4D]"
                  disabled={isVerifyingCode}
                  data-testid="verification-submit-button"
                >
                  {isVerifyingCode ? "Verifying..." : "Verify Code"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendCode}
                  className="h-12 rounded-full border-[#3ABDBC]/35 bg-[#121E4E] text-white hover:bg-[#17285D]"
                  disabled={isResendingCode}
                  data-testid="verification-resend-button"
                >
                  {isResendingCode ? "Resending..." : "Resend Code"}
                </Button>
              </div>

              <button
                type="button"
                onClick={closeVerificationModal}
                className="w-full text-sm font-medium text-slate-500 underline-offset-4 transition hover:text-slate-900 hover:underline"
                data-testid="verification-close-button"
              >
                Close for now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}